<template>
  <div class="goforgood">
    <div class="container">
      <div class="content">
        <div class="goforgood__content">
          <div class="container">
            <div class="goforgood__header center">
              <div class="goforgood__image">
                <img
                  :src="logoUrl"
                  class="img-responsive"
                  alt="Go For Good Logo"
                />
              </div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="goforgood__description" v-html="description"></div>
            </div>

            <div class="goforgood__dashboard">
              <div class="goforgood__dashboard-bg">
                <h2 class="goforgood__title">
                  {{ $t('go_for_good.dashboard.title') }}
                </h2>

                <div class="goforgood__dashboard-list">
                  <div class="goforgood__dashboard-item">
                    <div class="goforgood__dashboard-icon"></div>
                    <div class="goforgood__dashboard-text">
                      {{ formatPrice(Number(raisedAmount))
                      }}<small>{{
                        $t('go_for_good.dashboard.total_raised')
                      }}</small>
                    </div>
                  </div>
                  <div class="goforgood__dashboard-item">
                    <div class="goforgood__dashboard-icon"></div>
                    <div class="goforgood__dashboard-text">
                      {{ institutionsNumber
                      }}<small>
                        {{ $t('go_for_good.dashboard.institutions_count') }}
                      </small>
                    </div>
                  </div>
                  <div class="goforgood__dashboard-item">
                    <div class="goforgood__dashboard-icon"></div>
                    <div class="goforgood__dashboard-text">
                      {{ percentage }}%<small>{{
                        $t('go_for_good.dashboard.product_percentage')
                      }}</small>
                    </div>
                  </div>
                </div>

                <v-button
                  :label="
                    $t(
                      'go_for_good.dashboard.supported_institutions'
                    ).toUpperCase()
                  "
                  :block="true"
                  class="goforgood__dashboard-link"
                  @click="scrollToInstitutions"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="container">
          <div class="section section--universal">
            <h3 class="goforgood__title">
              {{ $t('go_for_good.collection.catalog_title') }}
            </h3>
            <products-grid
              :products="products"
              :go-for-good-catalog="true"
              :max-grid-columns="2"
            />
          </div>
        </div>
        <div ref="institutions" class="container">
          <div class="section section--universal">
            <h3 class="goforgood__title">
              {{ $t('go_for_good.collection.institutions_title') }}
            </h3>

            <ul class="goforgood__logos">
              <li
                v-for="institution in institutions"
                :key="institution.name"
                class="goforgood__logoitem"
              >
                <a
                  :href="institutionUrl(institution)"
                  target="_blank"
                  class="goforgood__logolink"
                >
                  <img :src="institution.image" class="img-responsive" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import isPresent from '~/utils/isPresent'
import priceable from '~/mixins/priceable'
import VButton from '~/components/v-button/VButton.vue'
import ProductsGrid from '~/components/ProductsGrid.vue'
export default {
  name: 'GoForGood',
  components: {
    VButton,
    ProductsGrid,
  },
  mixins: [priceable],
  props: {
    catalogText: {
      type: String,
      required: true,
    },
    commissionPercentage: {
      type: [String, Number],
      required: true,
    },
    raisedAmount: {
      type: [String, Number],
      required: true,
    },
    institutionsNumber: {
      type: [String, Number],
      required: true,
    },
    asset: {
      type: Object,
      required: true,
    },
    institutions: {
      type: Array,
      required: true,
    },
  },
  async fetch() {
    try {
      const storeInfo = this.$store.getters.storeInfo
      const params = {
        ...storeInfo,
        product_ids: this.productIds.join(','),
      }

      const { products } = await this.$axios.$get('/products/search', {
        params,
      })

      this.products = this.productsWithInstitutions(products)
    } catch (err) {
      this.$nuxt.context.error({
        statusCode: 404,
        message: 'Something went wrong',
      })
    }
  },
  data() {
    return {
      products: [],
    }
  },
  computed: {
    logoUrl() {
      return isPresent(this.asset?.url)
        ? this.asset?.url
        : '~/assets/images/goforgood/logo-goforgood.png'
    },
    description() {
      return isPresent(this.catalogText)
        ? this.catalogText
        : this.$t('go_for_good.collection.description')
    },
    productIds() {
      return this.institutions
        .map((i) => i.product_ids)
        .reduce((array, productIds) => {
          return array.concat(productIds)
        }, [])
    },
    percentage() {
      return parseInt(Number(this.commissionPercentage))
    },
  },
  methods: {
    institutionUrl(institution) {
      return isPresent(institution.link) ? institution.link : '#'
    },
    scrollToInstitutions() {
      this.$refs.institutions.scrollIntoView({ behavior: 'smooth' })
    },
    productsWithInstitutions(products) {
      return products.map((product) => {
        const productInstitution = this.institutions.find((institution) => {
          // eslint-disable-next-line camelcase
          return institution?.product_ids?.includes(product.id)
        })
        product.goForGoodProduct = true
        product.institutionName = productInstitution.name
        product.institutionDescription = productInstitution.short_description
        return product
      })
    },
  },
}
</script>

<style lang="scss">
.goforgood {
  > .container {
    width: 100%;
    padding: 0;
  }

  .content {
    padding: 0;
  }

  .taxon-title {
    display: none;
  }

  .products-grid {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -15px;
  }
}

.goforgood__content {
  margin-bottom: 34px;
  background: $color_gray url('~assets/images/goforgood/pattern-hearts.png')
    center;
  padding: 50px 0;

  > .container {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

.goforgood__header {
  margin-bottom: 50px;
}

.goforgood__image {
  width: 260px;
  margin: 0 auto 20px;
}

.goforgood__header-title {
  font-size: 22px;
  line-height: normal;
  display: block;
  margin: 10px 0 15px;
}

.goforgood__title {
  font-size: 22px;
  line-height: normal;
  display: block;
  margin: 0 0 20px;
}

.goforgood__dashboard {
  width: 100%;
  .goforgood__dashboard-bg {
    padding: 18px;
    height: 100%;
    background-color: white;
    border-radius: 8px;
    box-sizing: border-box;
  }

  .goforgood__dashboard-list {
    margin: 0 0 20px;
  }
  .goforgood__dashboard-item {
    font-weight: bold;
    font-size: 20px;
    display: flex;
    align-items: center;
    margin-bottom: 15px;

    .goforgood__dashboard-text {
      line-height: 18px;
    }

    small {
      font-weight: normal;
      font-size: 14px;
      display: block;
    }

    &:first-child .goforgood__dashboard-icon {
      background-image: url('~assets/images/goforgood/icon1.svg');
    }
    &:nth-child(2) .goforgood__dashboard-icon {
      background-image: url('~assets/images/goforgood/icon2.svg');
    }
    &:last-child .goforgood__dashboard-icon {
      background-image: url('~assets/images/goforgood/icon3.svg');
    }
  }

  .goforgood__dashboard-icon {
    width: 46px;
    height: 46px;
    margin-right: 20px;
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
  }
}

.goforgood__logos {
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  list-style: none;
  margin: 0 -10px;

  .goforgood__logoitem {
    width: 50%;
    padding: 0 10px;
    box-sizing: border-box;
    margin-bottom: 20px;

    img {
      display: block;
    }
  }

  .goforgood__logolink {
    display: block;
    border: 1px solid $color_gray;
  }
}

@media (min-width: $screen_tablet) {
  .goforgood {
    .products-grid {
      display: flex;
      margin: 0 -15px;
    }
  }

  .goforgood__content {
    padding: 40px 0;
  }

  .goforgood__header {
    width: calc(60% - 30px);
    margin-bottom: 0;
  }
  .goforgood__header-title {
    font-size: 32px;
  }

  .goforgood__dashboard {
    width: calc(40% - 30px);
    .goforgood__dashboard-bg {
      padding: 30px;
    }
  }

  .goforgood__logos {
    .goforgood__logoitem {
      width: 20%;
    }
  }
}
</style>
